import fpApiUtil from "./fp-api-util";

const BASE = "factoryplan/dashboard";

// FP 대시보드 엔드포인트는 전부 POST + form 필드 params(DashBoardParameter)다.
// 백엔드가 실제로 읽는 필드만 최소로 보낸다(대부분의 대시보드 서비스는 params를 안 읽음).
// 과거 DashBoardParameter.get()의 plain ObjectMapper로 전 요청이 405로 죽던 이력은
// backend-gaps.md #8 — 2026-08-11 백엔드 수정 완료(JavaTimeModule + FAIL_ON_UNKNOWN 해제).
const getWipEohOutRouteOutput = (params) => fpApiUtil.postParams(`${BASE}/wip-eoh-out/route-output`, params);
const getWipEohOutRouteWip = (params) => fpApiUtil.postParams(`${BASE}/wip-eoh-out/route-wip`, params);

// 5005 InOutStatusDashboard — 읽는 필드: 1/4번 routeGrpCd만, 2/3번 routeGrpCd+planScope+viewId.
// planScope는 보내지 않는다(old 동일) — 기간 시드(DASHBOARD_SRH_RANGE)가 PLAN_SCOPE='DEFAULT'뿐이라
// 보내면 스코프 필터링으로 D/W/M 기간이 사라져 shipment-status가 NPE(클라이언트 405 변장)로 죽는다.
const getInOutRouteGrpTargetActl = (params) => fpApiUtil.postParams(`${BASE}/inout-status/route-grp/target-actl`, params);
const getInOutShipmentStatus = (params) => fpApiUtil.postParams(`${BASE}/inout-status/shipment-status`, params);
const getInOutStatus = (params) => fpApiUtil.postParams(`${BASE}/inout-status`, params);
const getInOutWeeklyShipment = (params) => fpApiUtil.postParams(`${BASE}/inout-status/weekly-shipment`, params);
// ROUTE_GRP 콤보 옵션(old common.js getRouteGrpOptions) — TB_FP_ROUTE_IN_OUT의 distinct routeGrpCd.
// 얘만 LookupQueryParameter 경로라 planScope를 읽는다(미전송 시 DEFAULT 폴백).
const getAnalysisRouteGroups = (params) => fpApiUtil.postParams("factoryplan/analysis/routegroups", params);

// 5008 SimulationKPIDashboard — 7개 전부 LookupQueryParameter 경로(DashBoardParameter 아님).
// params 공통: { planScope, versionCd, plantCds } — plantCd 배열을 서버 필드명 plantCds로 보낸다.
// versionCd는 서버 where에 isNotEmpty 가드 없이 .eq()라(SimulationKpiQueryRepository) 버전 확정
// 후에만 호출한다. old가 같이 보내던 localeDateFormat은 이 7개 경로가 안 읽어 전송 제외(§A-3).
const getSimulationKpiFulfillRate = (params) => fpApiUtil.postParams("factoryplan/simulation/simulation-kpi/ful-fill-rate", params);
const getSimulationKpiOnTimeRate = (params) => fpApiUtil.postParams("factoryplan/simulation/simulation-kpi/on-time-rate", params);
const getSimulationKpiLeadTime = (params) => fpApiUtil.postParams("factoryplan/simulation/simulation-kpi/lead-time", params);
// bed/worker는 백엔드가 동일 구현(getResourceUtilization(qp, true))이라 응답이 100% 같다 —
// old 백엔드도 동일했으므로(이관 회귀 아님) 두 카드가 같은 차트를 그리는 게 정상 동작이다.
const getSimulationKpiUsedRateBed = (params) => fpApiUtil.postParams("factoryplan/simulation/simulation-kpi/used-rate/bed", params);
const getSimulationKpiUsedRateWorker = (params) => fpApiUtil.postParams("factoryplan/simulation/simulation-kpi/used-rate/worker", params);
// URL의 {version-cd}는 @PathVariable로 바인딩되지 않는 장식 세그먼트(서버는 params.versionCd만
// 읽음) — old가 양쪽 다 넣어 보내던 계약을 그대로 유지한다.
const getProblemKpi = (versionCd, params) =>
  fpApiUtil.postParams(`factoryplan/analysis/problem-analysis/versions/${encodeURIComponent(versionCd)}/kpi`, params);
const getProblemStatusDetail = (versionCd, params) =>
  fpApiUtil.postParams(`factoryplan/analysis/problem-analysis/versions/${encodeURIComponent(versionCd)}/problem-status/detail`, params);
// 5009 PlanProblemDashboard — 위 두 개와 같은 problem-analysis 계열(LookupQueryParameter, 같은 params).
// 응답은 { deliveryDelayStatus: {<사유코드>: 건수}, planFailStatus: {...} } 두 맵이고 서버가
// LinkedHashMap으로 **건수 내림차순 삽입 순서**를 보장한다(ProblemAnalysisService) — 클라에서 재정렬 금지.
const getProblemStatus = (versionCd, params) =>
  fpApiUtil.postParams(`factoryplan/analysis/problem-analysis/versions/${encodeURIComponent(versionCd)}/problem-status`, params);

// 5001 FpVersusPerformance — 3개 전부 LookupQueryParameter 경로(DashBoardParameter 아님).
// params: { planScope }만 전송 — 오더 적재 정책이 old와 같은 "현재 릴리즈 1세트"라(2026-08-25
// fp 스키마 실측·사용자 판정) old ETL처럼 버전 무관 집계한다. 서버의 versionCd/plantCds
// isNotEmpty 가드는 남아 있어, 오더를 버전별로 적재하는 정책으로 바뀌면 필드만 실으면 된다.
// kpi 응답 1건을 타일 3개가 공유한다(old는 같은 프로시저 브랜치를 위젯 3개가 3중 호출 — 교정).
// routePosition: START=최초 투입 공정 / END=완성품 공정(@PathVariable 바인딩 — 장식 세그먼트 아님).
const getFpVersusPerformanceKpi = (params) => fpApiUtil.postParams(`${BASE}/fp-versus-performance/kpi`, params);
const getFpVersusPerformancePoPlanActual = (routePosition, params) =>
  fpApiUtil.postParams(`${BASE}/fp-versus-performance/po-plan-actual/${encodeURIComponent(routePosition)}`, params);
const getFpVersusPerformanceItemPlanActual = (params) => fpApiUtil.postParams(`${BASE}/fp-versus-performance/item-plan-actual`, params);

const fpDashboardService = {
  getWipEohOutRouteOutput,
  getWipEohOutRouteWip,
  getInOutRouteGrpTargetActl,
  getInOutShipmentStatus,
  getInOutStatus,
  getInOutWeeklyShipment,
  getAnalysisRouteGroups,
  getSimulationKpiFulfillRate,
  getSimulationKpiOnTimeRate,
  getSimulationKpiLeadTime,
  getSimulationKpiUsedRateBed,
  getSimulationKpiUsedRateWorker,
  getProblemKpi,
  getProblemStatusDetail,
  getProblemStatus,
  getFpVersusPerformanceKpi,
  getFpVersusPerformancePoPlanActual,
  getFpVersusPerformanceItemPlanActual,
};
export default fpDashboardService;
